import { Link, useParams } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";

import { ALTERNATIVE_PAGES } from "@/lib/blog-data";
import logoImg from "@assets/Untitled_design_(9)_1773925932166.png";
import logoDarkImg from "@assets/Untitled_design_(10)_1773926263699.png";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown, Check, X, Minus } from "lucide-react";
import { useEffect } from "react";
import { SeoPageFooter } from "@/components/seo-page-footer";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function ComparisonCell({ value }: { value: string }) {
  const isNo = value === "No";
  const isYes = value.startsWith("Yes");
  return (
    <span
      className={
        isYes
          ? "text-green-600 dark:text-green-400 font-medium"
          : isNo
          ? "text-red-500 dark:text-red-400"
          : "text-foreground"
      }
    >
      {value}
    </span>
  );
}

export default function AlternativePage() {
  const { slug } = useParams<{ slug: string }>();
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDarkImg : logoImg;

  const page = ALTERNATIVE_PAGES.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Page not found</h1>
          <Link href="/blog">
            <Button>Back to resources</Button>
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": page.title,
    "description": page.metaDescription,
    "datePublished": page.publishedDate,
    "dateModified": page.modifiedDate,
    "author": { "@type": "Organization", "name": "The Proper Tool" },
    "publisher": { "@type": "Organization", "name": "The Proper Tool", "url": "https://thepropertool.com" },
    "url": `https://thepropertool.com/alternatives/${page.slug}`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Comparisons",
        "item": "https://thepropertool.com/comparisons"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": page.competitor,
        "item": `https://thepropertool.com/alternatives/${page.slug}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title={page.metaTitle}
        description={page.metaDescription}
        canonical={`/alternatives/${page.slug}`}
        ogType="article"
        articlePublished={page.publishedDate}
        articleModified={page.modifiedDate}
        schema={[articleSchema, breadcrumbSchema, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <header className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <img src={logo} alt="The Proper Tool" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-4">
              <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
              <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog &amp; Guides</Link>
              <Link href="/comparisons" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Comparisons</Link>
              <div className="relative group">
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  The App <ChevronDown className="h-3 w-3" />
                </button>
                <div className="absolute top-full right-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-background border border-border rounded-lg shadow-lg min-w-60 py-1">
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Product Features</p>
                    <Link href="/quoting-software-for-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Auto Quote</Link>
                    <Link href="/estimating-software-for-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Estimating Software</Link>
                    <Link href="/construction-estimating-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Cost Estimating</Link>
                    <Link href="/quoting-app-for-builders" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Mobile App</Link>
                    <div className="border-t border-border my-1" />
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Who It's For</p>
                    <Link href="/quoting-software-for-contractors" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Contractors</Link>
                    <Link href="/tradesman-quoting-tool" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Tradesmen</Link>
                    <Link href="/job-quoting-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">For Job Quoting</Link>
                    <div className="border-t border-border my-1" />
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/60">Compare</p>
                    <Link href="/best-quoting-software" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Compare Tools</Link>
                    <Link href="/tradify-alternative" className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Tradify Alternative</Link>
                  </div>
                </div>
              </div>
            </div>
            <Button size="sm" asChild>
              <Link href="/login">Sign in</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
          <ChevronRight className="h-4 w-4" />
          <span>{page.competitor} Alternative</span>
        </div>

        <div className="mb-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">Comparison</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" data-testid="text-alt-title">
          {page.title}
        </h1>

        <p className="text-xl text-muted-foreground leading-relaxed mb-2">
          Updated {formatDate(page.modifiedDate)}
        </p>

        <div className="mt-8 mb-10 pb-8 border-b border-border/60">
          <p className="text-lg leading-relaxed text-foreground/90">{page.intro}</p>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">What is {page.competitor}?</h2>
          <p className="text-base sm:text-lg leading-relaxed text-foreground/90">{page.competitorOverview}</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">{page.competitor} limitations for UK builders</h2>
          <ul className="space-y-3">
            {page.competitorLimitations.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-base sm:text-lg leading-relaxed text-foreground/90">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-6">
            The Proper Tool vs {page.competitor}: Feature Comparison
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                  <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">{page.competitor}</th>
                  <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                </tr>
              </thead>
              <tbody>
                {page.comparisonRows.map((row, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                    <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                    <td className="px-5 py-4 text-sm"><ComparisonCell value={row.competitor} /></td>
                    <td className="px-5 py-4 text-sm"><ComparisonCell value={row.us} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Pricing comparison</h2>
          <p className="text-base sm:text-lg leading-relaxed text-foreground/90">{page.pricingComparison}</p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">
            Reasons builders switch from {page.competitor} to The Proper Tool
          </h2>
          <ul className="space-y-3">
            {page.switchReasons.map((reason, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-base sm:text-lg leading-relaxed text-foreground/90">{reason}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Our verdict</h2>
          <p className="text-base sm:text-lg leading-relaxed text-foreground/90">{page.conclusion}</p>
        </section>

        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Try The Proper Tool free</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Builder quoting software built specifically for UK residential construction. Generate a professional quote in under 10 minutes.
          </p>
          <Button size="lg" asChild>
            <Link href="/login">Start free, no card needed</Link>
          </Button>
          <p className="text-xs text-muted-foreground mt-3">14-day free trial. Plans from £29/mo. No credit card required.</p>
        </div>

        <div className="mt-8 border border-border/40 rounded-xl p-5 bg-muted/30">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed since then. We recommend verifying current details directly with each provider before making a purchasing decision.
          </p>
        </div>

        {page.relatedGuides && page.relatedGuides.length > 0 && (
          <div className="mt-8 border border-border/60 rounded-xl p-5 bg-muted/20">
            <p className="text-sm font-semibold text-foreground mb-3">Related guides</p>
            <div className="flex flex-col gap-2">
              {page.relatedGuides.map((guide) => (
                <Link
                  key={guide.href}
                  href={guide.href}
                  className="flex items-center gap-2 text-primary hover:underline text-sm font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                  {guide.title}
                </Link>
              ))}
            </div>
          </div>
        )}

        {page.relatedBlogPost && (
          <div className="mt-6 border border-border/60 rounded-xl p-5 bg-muted/20">
            <p className="text-sm font-semibold text-foreground mb-2">Related reading</p>
            <Link
              href={`/blog/${page.relatedBlogPost.slug}`}
              className="flex items-center gap-2 text-primary hover:underline text-sm font-medium"
            >
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
              {page.relatedBlogPost.title}
            </Link>
          </div>
        )}

        {page.relatedAlternatives && page.relatedAlternatives.length > 0 && (
          <div className="mt-6 border border-border/60 rounded-xl p-5 bg-muted/20">
            <p className="text-sm font-semibold text-foreground mb-3">More comparisons</p>
            <div className="flex flex-wrap gap-2">
              {page.relatedAlternatives.map((alt) => (
                <Link
                  key={alt.slug}
                  href={`/alternatives/${alt.slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition-colors font-medium"
                >
                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                  The Proper Tool vs {alt.competitor}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link href="/comparisons" className="text-sm text-primary hover:underline flex items-center gap-1 justify-center">
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to all comparisons
          </Link>
        </div>
      </main>

      <SeoPageFooter />
    </div>
  );
}
